import type { FC } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Toaster } from 'react-hot-toast'
import 'swiper/css'
import { TaskStatusDetailModal } from '~/components/modals/TaskStatusDetailsModal'
import { isSuccessTerminated, isTerminated } from '~/constants/task-status'
import { getCampaignAgentType, useConversationUuid, useTaskStatusFromTaskObj } from '~/hooks'
import { useMaxCapProgress } from '~/hooks/useMaxCapProgress'
import { CommonModal, ModalId, useModalController } from '~/hooks/useModal'
import { useSource } from '~/hooks/useSource'
import {
  AgentRequest,
  CommandType,
  SourceOutputExamples,
  agentTaskStatusFromJSON,
  sourceOutputExampleTypeFromJSON
} from '~/protos/agents'
import { trace } from '~/services/analyticsService'
import { submitCampaignTask } from '~/services/campaignService'
import { SocketEvent } from '~/services/socket'
import store from '~/store'
import { setTaskObj } from '~/store/chatSlice'
import { ConversationWithMessages } from '~/types/Conversations'
import { AppEvents, globalEventEmitter } from '~/utils/shared'
import { getDeviceId } from '~/utils/storage'
import { BetterToast } from '~/utils/toast'
import WelcomeBackground from '../../assets/campaign/WelcomeBackground.png'
import { IconCurieStar, IconStartTrialCampaign } from '../icons'
import {
  EdiSchemaData,
  createEdiSchema
} from '~/components/task-forms/shared/input-output-form/Edi'
import {
  EdifactSchemaData,
  createEdifactSchema
} from '~/components/task-forms/shared/input-output-form/EdiFact'
import {
  Hl7SchemaData,
  createHl7Schema
} from '~/components/task-forms/shared/input-output-form/Hl7'
import { CampaignButton } from './CampaignButton'
import CampaignFooter from './CampaignFooter'
import CampaignNavBar from './CampaignNavBar'
import CampaignRedirect from './CampaignRedirect'
import CampaignTryExample from './CampaignTryExample'
import CampaignTryOwnData from './CampaignTryOwnData'
import { CampaignWhiteButton } from './CampaignWhiteButton'
import TryAgentsSection from './TryAgentsSection'
import { Message } from '../chat/message'

interface CampaignSiteLayoutProps {
  title: string
  description: string
  instructionsTitle?: string
  instructions?: React.ReactNode
  maxTimeCap?: number
  isDisabled?: boolean
  buttonText?: string
}

export const CampaignSiteLayout: FC<CampaignSiteLayoutProps> = ({
  title,
  description,
  instructionsTitle,
  instructions,
  maxTimeCap = 50,
  buttonText = 'Generate'
}) => {
  const [isDisabled, setIsDisabled] = useState(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [exampleRequest, setExampleRequest] = useState<AgentRequest>(AgentRequest.fromPartial({}))
  const [ownDatarequest, setOwnDataRequest] = useState<AgentRequest>(AgentRequest.fromPartial({}))
  const [taskId, setTaskId] = useState<number | null>(null)
  const taskStatus = useTaskStatusFromTaskObj()
  const [messages, setMessages] = useState<Message[]>([])
  const [showInstructions, setShowInstructions] = useState(true)
  const pageRef = useRef<HTMLDivElement>(null)
  const { progress: taskProgress, reset: resetTaskProgress } = useMaxCapProgress({
    isDone: isSuccessTerminated(taskStatus),
    maxTimeCap,
    taskStatus
  })

  const command = getCampaignAgentType()
  const renderWelcomeText = () => {
    switch (command) {
      case CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION:
        return 'Try DataWeave Generator agent from sample input and output data'
      case CommandType.GENERATE_IMAGE:
        return 'Try Diagram Generator agent from sample input'
      default:
        return 'Try DataWeave Generator agent from sample input and output data'
    }
  }

  const exampleRef = useRef<null | HTMLDivElement>(null)
  const ownDataRef = useRef<null | HTMLDivElement>(null)

  // {'example', 'ownData'}
  const [currentGenerationSection, setCurrentGenerationSection] = useState<string>('example')

  // Schema specific state for example section
  const [exampleSourceDataType, setExampleSourceDataType] = useState<string>('')
  const [exampleEdiSchemaData, setExampleEdiSchemaData] = useState<EdiSchemaData | null>(null)
  const [exampleEdifactSchemaData, setExampleEdifactSchemaData] =
    useState<EdifactSchemaData | null>(null)
  const [exampleHl7SchemaData, setExampleHl7SchemaData] = useState<Hl7SchemaData | null>(null)

  // Schema specific state for own data section
  const [ownDataSourceDataType, setOwnDataSourceDataType] = useState<string>('')
  const [ownDataEdiSchemaData, setOwnDataEdiSchemaData] = useState<EdiSchemaData | null>(null)
  const [ownDataEdifactSchemaData, setOwnDataEdifactSchemaData] =
    useState<EdifactSchemaData | null>(null)
  const [ownDataHl7SchemaData, setOwnDataHl7SchemaData] = useState<Hl7SchemaData | null>(null)

  const handleChangeGenerationSection = (section: string) => {
    setCurrentGenerationSection(section)
  }

  const handleSetExampleRequest = (request: AgentRequest) => {
    // Update the request to include schema data if applicable
    if (exampleSourceDataType === 'DATA_TYPE_EDI' && exampleEdiSchemaData) {
      const updatedExamples = request.sourceOutputExamples?.map(example => {
        const newExample = SourceOutputExamples.fromPartial({
          sourceData: example.sourceData,
          outputData: example.outputData,
          sourceDataType: sourceOutputExampleTypeFromJSON(exampleSourceDataType),
          inputEdiSchemaType: createEdiSchema(exampleEdiSchemaData)
        })
        return newExample
      })
      request.sourceOutputExamples = updatedExamples
    } else if (exampleSourceDataType === 'DATA_TYPE_EDIFACT' && exampleEdifactSchemaData) {
      const updatedExamples = request.sourceOutputExamples?.map(example => {
        const newExample = SourceOutputExamples.fromPartial({
          sourceData: example.sourceData,
          outputData: example.outputData,
          sourceDataType: sourceOutputExampleTypeFromJSON(exampleSourceDataType),
          inputEdiFactSchemaType: createEdifactSchema(exampleEdifactSchemaData)
        })
        return newExample
      })
      request.sourceOutputExamples = updatedExamples
    } else if (exampleSourceDataType === 'DATA_TYPE_HL7' && exampleHl7SchemaData) {
      const updatedExamples = request.sourceOutputExamples?.map(example => {
        const newExample = SourceOutputExamples.fromPartial({
          sourceData: example.sourceData,
          outputData: example.outputData,
          sourceDataType: sourceOutputExampleTypeFromJSON(exampleSourceDataType),
          inputHl7SchemaType: createHl7Schema(exampleHl7SchemaData)
        })
        return newExample
      })
      request.sourceOutputExamples = updatedExamples
    }

    setExampleRequest(request)
  }

  const handleSetOwnDataRequest = (request: AgentRequest) => {
    // Update the request to include schema data if applicable
    if (ownDataSourceDataType === 'DATA_TYPE_EDI' && ownDataEdiSchemaData) {
      const updatedExamples = request.sourceOutputExamples?.map(example => {
        const newExample = SourceOutputExamples.fromPartial({
          sourceData: example.sourceData,
          outputData: example.outputData,
          sourceDataType: sourceOutputExampleTypeFromJSON(ownDataSourceDataType),
          inputEdiSchemaType: createEdiSchema(ownDataEdiSchemaData)
        })
        return newExample
      })
      request.sourceOutputExamples = updatedExamples
    } else if (ownDataSourceDataType === 'DATA_TYPE_EDIFACT' && ownDataEdifactSchemaData) {
      const updatedExamples = request.sourceOutputExamples?.map(example => {
        const newExample = SourceOutputExamples.fromPartial({
          sourceData: example.sourceData,
          outputData: example.outputData,
          sourceDataType: sourceOutputExampleTypeFromJSON(ownDataSourceDataType),
          inputEdiFactSchemaType: createEdifactSchema(ownDataEdifactSchemaData)
        })
        return newExample
      })
      request.sourceOutputExamples = updatedExamples
    } else if (ownDataSourceDataType === 'DATA_TYPE_HL7' && ownDataHl7SchemaData) {
      const updatedExamples = request.sourceOutputExamples?.map(example => {
        const newExample = SourceOutputExamples.fromPartial({
          sourceData: example.sourceData,
          outputData: example.outputData,
          sourceDataType: sourceOutputExampleTypeFromJSON(ownDataSourceDataType),
          inputHl7SchemaType: createHl7Schema(ownDataHl7SchemaData)
        })
        return newExample
      })
      request.sourceOutputExamples = updatedExamples
    }

    setOwnDataRequest(request)
  }

  const handleSetError = (error: boolean) => {
    setIsError(error)
  }

  const handleTryExample = () => {
    setTimeout(() => {
      if (!exampleRef.current) return
      exampleRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
  }

  const handleTryOwn = () => {
    setTimeout(() => {
      if (!ownDataRef.current) return
      ownDataRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
  }

  const redirectToCurieTrialPage = () => {
    window.open('https://platform.curietech.ai/signup', '_blank')
  }

  // return false if any of the fields are not filled
  const checkFieldsFilled = (request: AgentRequest) => {
    if (command === CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION) {
      for (const inputOutputExample of request.sourceOutputExamples ?? []) {
        if (
          inputOutputExample.sourceData == null ||
          inputOutputExample.outputData == null ||
          inputOutputExample.sourceData?.length === 0 ||
          inputOutputExample.outputData?.length === 0
        ) {
          BetterToast.error('Please fill in all fields')
          trace('campaign_task_fields_not_filled')
          return false
        }
      }
      return true
    } else if (command === CommandType.GENERATE_IMAGE) {
      return !!(
        request.command?.commandString && request.metadata?.codeContext?.s3TarFilePath?.length
      )
    }
  }

  useEffect(() => {
    trace('campaign_task_exposure')
  }, [])
  useSource()

  const { openModal: openSignUpModal, closeModal: closeSignUpModal } = useModalController(
    ModalId.SignupModal
  )

  const preSubmitCheck = (request: AgentRequest, section = 'example') => {
    trace('campaign_task_submit')
    if (!checkFieldsFilled(request)) {
      trace('campaign_task_fields_not_filled')
      BetterToast.error('Please fill in all fields')
      return false
    }

    if (section === 'ownData' && localStorage.getItem('registered') !== 'true') {
      openSignUpModal({
        onSuccess() {
          closeSignUpModal()
        }
      })
      return false
    }
    return true
  }

  const handleSubmit = async (request: AgentRequest, isExample?: boolean) => {
    store.dispatch(setTaskObj({ uuid, task: null }))
    setMessages([])
    setIsDisabled(true)

    request.command!.commandType = command
    request.deviceId = getDeviceId()

    try {
      const task = await submitCampaignTask(request)
      setTaskId(task.taskId)
      trace({
        event: 'campaign_task_submitted',
        labels: {
          task_id: task.taskId.toString(),
          is_example: String(isExample ?? false)
        }
      })
    } catch (err) {
      const error = err as Error
      setIsDisabled(false)
      if (error.message.includes('Usage limit exceeded')) {
        trace('waitlist_pre_exposure')
        openSignUpModal({
          onSuccess() {
            closeSignUpModal()
          }
        })
        return
      }
      trace({
        event: 'campaign_task_submit_failed',
        labels: {
          error: error.message
        }
      })
      console.error('Failed to submit task', error)
      BetterToast.error('Failed to submit task')
    }
  }
  const uuid = useConversationUuid()

  const renderFreeTrialButton = () => {
    return (
      <div className='w-10/12 flex justify-end mx-auto'>
        <CampaignButton
          className='px-4 py-6 mt-2 mb-10 rounded-full disabled:opacity-60'
          onClick={redirectToCurieTrialPage}
        >
          <div className='flex flex-row gap-2 items-center'>
            <span className='text-xl font-normal'>Try More Curie Agents</span>
            <IconStartTrialCampaign />
          </div>
        </CampaignButton>
      </div>
    )
  }

  useEffect(() => {
    const onConversationUpdated = (data: {
      data: Pick<ConversationWithMessages, 'messages' | 'task'>
      event: string
    }) => {
      if (data.event !== SocketEvent.ConversationUpdated) return
      const { messages, task } = data.data
      store.dispatch(setTaskObj({ uuid, task }))
      setMessages(messages)
      if (isTerminated(agentTaskStatusFromJSON(task.status))) {
        setIsDisabled(false)
        trace({
          event: 'campaign_task_completed',
          labels: {
            task_id: String(task.task_id),
            task_status: task.status
          }
        })
      }
    }
    globalEventEmitter.on(AppEvents.SocketEvent, onConversationUpdated)
    return () => {
      globalEventEmitter.off(AppEvents.SocketEvent, onConversationUpdated)
    }
  }, [taskId])

  const progress = isSuccessTerminated(taskStatus) ? 100 : taskProgress

  const handleExampleSourceDataTypeChange = (dataType: string) => {
    setExampleSourceDataType(dataType)
  }

  const handleOwnDataSourceDataTypeChange = (dataType: string) => {
    setOwnDataSourceDataType(dataType)
  }

  return (
    <div>
      {/* welcome page */}
      <div className='absolute inset-0 z-0 top-0 right-0'>
        <img src={WelcomeBackground} className='w-screen h-screen object-cover' />
      </div>
      <div className='flex-1 flex flex-col items-center w-screen h-full absolute top-0 right-0 z-10'>
        <CampaignNavBar />
        <div className='max-w-screen w-full'>
          <div className='w-full h-[80vh] max-h-[80vh] flex items-center justify-center caret-transparent bg-transparent'>
            <div className='text-center p-8 mx-auto my-auto'>
              <p className='text-2xl mb-3 flex items-center justify-center'>
                <IconCurieStar className='w-7 h-7' />
                <span className='ml-1 tracking-tighter font-medium'>CURIE AI</span>
              </p>
              <h1 className='text-9xl mb-8 flex items-center justify-center font-normal tracking-tighter'>
                Welcome to&nbsp;
                <span className='bg-gradient-to-r from-[#8063FA] to-[#C8A7FF] text-transparent bg-clip-text'>
                  Curie AI
                </span>
                &nbsp;Test Drive
              </h1>
              <p className='text-3xl text-gray-600 mb-12 mx-auto'>
                The simplest and fastest way to experience Curie AI agents. Curie Test Drive is an
                environment that allows you to try out selected agents.
              </p>
              <p className='text-3xl mb-8 font-medium'>{renderWelcomeText()}</p>
              <div className='space-x-4'>
                <CampaignButton onClick={handleTryExample} className='w-60 h-14'>
                  Try Example
                </CampaignButton>
                <CampaignWhiteButton onClick={handleTryOwn} className='w-60 h-14'>
                  Try with your own data
                </CampaignWhiteButton>
              </div>
            </div>
          </div>

          {/* try our example page */}
          <CampaignTryExample
            commandType={command}
            currentGenerationSection={currentGenerationSection}
            exampleRef={exampleRef}
            handleChangeGenerationSection={handleChangeGenerationSection}
            handleSetError={handleSetError}
            handleSetRequest={handleSetExampleRequest}
            handleSubmit={handleSubmit}
            handleTryOwn={handleTryOwn}
            isDisabled={isDisabled}
            isError={isError}
            messages={messages}
            preSubmitCheck={preSubmitCheck}
            progress={progress}
            request={exampleRequest}
            resetTaskProgress={resetTaskProgress}
            taskStatus={taskStatus}
            sourceDataType={exampleSourceDataType}
            onSourceDataTypeChange={handleExampleSourceDataTypeChange}
            // Schema components
            ediSchemaData={exampleEdiSchemaData}
            onEdiSchemaChange={setExampleEdiSchemaData}
            edifactSchemaData={exampleEdifactSchemaData}
            onEdifactSchemaChange={setExampleEdifactSchemaData}
            hl7SchemaData={exampleHl7SchemaData}
            onHl7SchemaChange={setExampleHl7SchemaData}
          />
          {renderFreeTrialButton()}

          {/* try own data page */}
          <CampaignTryOwnData
            commandType={command}
            currentGenerationSection={currentGenerationSection}
            handleChangeGenerationSection={handleChangeGenerationSection}
            handleSetError={handleSetError}
            handleSetRequest={handleSetOwnDataRequest}
            handleSubmit={handleSubmit}
            handleTryExample={handleTryExample}
            isDisabled={isDisabled}
            isError={isError}
            messages={messages}
            ownDataRef={ownDataRef}
            preSubmitCheck={preSubmitCheck}
            progress={progress}
            request={ownDatarequest}
            resetTaskProgress={resetTaskProgress}
            taskStatus={taskStatus}
            sourceDataType={ownDataSourceDataType}
            onSourceDataTypeChange={handleOwnDataSourceDataTypeChange}
            // Schema components
            ediSchemaData={ownDataEdiSchemaData}
            onEdiSchemaChange={setOwnDataEdiSchemaData}
            edifactSchemaData={ownDataEdifactSchemaData}
            onEdifactSchemaChange={setOwnDataEdifactSchemaData}
            hl7SchemaData={ownDataHl7SchemaData}
            onHl7SchemaChange={setOwnDataHl7SchemaData}
          />
          {renderFreeTrialButton()}

          <div className='w-full h-fit flex items-center justify-center caret-transparent'>
            <TryAgentsSection />
          </div>
          <div>
            <CampaignFooter />
          </div>
        </div>
      </div>
      <CommonModal
        id={ModalId.TaskStatusDetails}
        Component={TaskStatusDetailModal}
        className='fixed'
        contentClassName='h-[65vh]'
      />
      <CommonModal
        id={ModalId.SignupModal}
        Component={CampaignRedirect}
        className='fixed'
        contentClassName='w-[450px] h-[275px]'
      />
      <Toaster containerStyle={{ zIndex: 99999 }} />
    </div>
  )
}
